import styled from 'styled-components';

import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { asteriskLabel } from '@utils/strings';

import { useFormBuilderContext } from './context';
import { getFieldConfigForKind } from './field-registry';
import { StateField } from './state';

export const useHasStaleConditionOption = (field: StateField | null | undefined): boolean => {
  const { predefinedOptionsLists, marketplaceIntegrationLists, variableGroups, store } =
    useFormBuilderContext();
  return store.useHasStaleConditionOption(field?.id ?? '', {
    predefinedOptionsLists,
    marketplaceIntegrationLists,
    variableGroups,
  });
};

export const StaleConditionWarningIcon = ({
  field,
  dataTestid = `formbuilder-stale-condition-${field.id}`,
}: {
  field: StateField;
  dataTestid?: string;
}) => {
  const { t } = useScopedTranslate(staleConditionLocale);
  const hasStaleConditionOption = useHasStaleConditionOption(field);
  if (!hasStaleConditionOption) return null;

  return (
    <IconFormIndicatorWarning
      size={16}
      role="img"
      // Short on purpose: inline in a <label>, this folds into the previewed
      // input's accessible name. The full sentence stays in the tooltip.
      aria-label={t('staleConditionName')}
      tooltip={t('staleConditionOption')}
      // pointerEvents: the unselected canvas preview is wrapped in
      // pointerEvents:'none'. position/zIndex: a section's absolutely
      // positioned TopDropzone otherwise paints over the icon. Both would
      // leave the tooltip unopenable.
      style={{ pointerEvents: 'auto', position: 'relative', zIndex: 1 }}
      dataTestid={dataTestid}
    />
  );
};

/**
 * A required element previews as asteriskLabel('', true), " *", so even a blank
 * label renders one and a corner badge would be a second icon.
 */
const rendersLabelText = (field: StateField): boolean => {
  // Content kinds render no FormLabel. Read off the registry so a new content kind is covered.
  if (getFieldConfigForKind(field.kind).group === 'content') return false;
  if (!('label' in field)) return false;
  if (typeof field.label !== 'string') return Boolean(field.label);
  return Boolean(asteriskLabel(field.label, 'isRequired' in field && Boolean(field.isRequired)));
};

export const StaleConditionWarning = ({ field }: { field: StateField }) => {
  const { store } = useFormBuilderContext();
  const isSelected = store.useIsFieldSelected(field.id);
  const hasStaleConditionOption = useHasStaleConditionOption(field);
  const hasConditionsTab = field.builderProps?.canEditConditions ?? true;
  const hasLabelSlot = rendersLabelText(field);
  // Selecting an element swaps its label preview for the edit form, so the
  // corner is the only place left when there is also no Conditions tab.
  const needsCorner = isSelected ? !hasConditionsTab : !hasLabelSlot;
  if (!hasStaleConditionOption || !needsCorner) {
    return null;
  }

  return (
    <StaleConditionIndicator>
      <StaleConditionWarningIcon field={field} />
    </StaleConditionIndicator>
  );
};

const StaleConditionIndicator = styled.span({
  position: 'absolute',
  top: pxToRem(8),
  right: pxToRem(8),
  display: 'flex',
  zIndex: 1,
});

export const staleConditionLocale = {
  en: {
    staleConditionName: 'Condition warning',
    staleConditionOption:
      'A condition on this element compares against an option that no longer exists. Open it and pick a new value — until you do, this condition will never match.',
    staleConditionTab: 'A condition on this element uses an option that no longer exists',
  },
};
